import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const API_URL = API_BASE_URL ? `${API_BASE_URL}/api` : '/api';
const isDev = process.env.NODE_ENV === 'development';

// Cache le token en mémoire pour éviter de parser localStorage à chaque requête
let cachedToken: string | null = null;
let tokenCacheTime = 0;
const TOKEN_CACHE_TTL = 30000; // 30s

export function getToken(): string | null {
  const now = Date.now();
  if (cachedToken && now - tokenCacheTime < TOKEN_CACHE_TTL) {
    return cachedToken;
  }
  try {
    const authData = localStorage.getItem('auth-storage');
    if (authData) {
      const parsed = JSON.parse(authData);
      cachedToken = parsed.state?.token || null;
      tokenCacheTime = now;
    }
  } catch {
    cachedToken = null;
  }
  return cachedToken;
}

// Invalider le cache token (appelé au logout)
export function clearTokenCache() {
  cachedToken = null;
  tokenCacheTime = 0;
}

export const api = axios.create({
  baseURL: API_URL,
  timeout: 15000, // 15s — évite les requêtes pendues sur réseau faible
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter automatiquement le token
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Espace client multi-comptes : profil actif sélectionné (switch). Le backend ne l'accepte que
  // s'il fait partie des clients accessibles de l'utilisateur (validation serveur-side).
  try {
    const activeClient = localStorage.getItem('nudge-active-client');
    if (activeClient) config.headers['x-active-client'] = activeClient;
  } catch {
    /* localStorage indispo — pas grave */
  }
  // Anti-écho temps-réel : on transmet l'id du socket courant. Le backend exclut ce socket
  // de la diffusion de l'event correspondant (on ne reçoit pas notre propre mutation).
  // Import paresseux pour éviter une dépendance circulaire api ↔ socket.
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { getSocketId } = require('./socket');
    const sid = getSocketId?.();
    if (sid) config.headers['x-socket-id'] = sid;
  } catch {
    /* socket pas encore initialisé — pas grave */
  }
  if (isDev) {
    console.log(`API ${config.method?.toUpperCase()} ${config.url}`);
  }
  return config;
});

// Flag pour éviter les boucles infinies de refresh
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

function processQueue(error: any, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  failedQueue = [];
}

// Intercepteur pour gérer les erreurs d'authentification avec refresh automatique
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isDev) {
      console.error(`API Error ${error.response?.status}: ${error.config?.url}`);
    }

    const originalRequest = error.config;

    // Si 401 et pas déjà un retry, tenter le refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Ne pas refresh si c'est déjà la route refresh-token ou login
      if (originalRequest.url?.includes('/auth/refresh-token') || originalRequest.url?.includes('/auth/login')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Si un refresh est en cours, mettre la requête en file d'attente
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Récupérer le refresh token depuis le localStorage
        let authData: string | null = null;
        let refreshToken: string | null = null;
        try {
          authData = localStorage.getItem('auth-storage');
          refreshToken = authData ? JSON.parse(authData).state?.refreshToken : null;
        } catch {
          // localStorage corrompu
          refreshToken = null;
        }

        if (!refreshToken) {
          throw new Error('Pas de refresh token');
        }

        const response = await axios.post(`${API_URL}/auth/refresh-token`, { refreshToken });
        const { token: newToken } = response.data;

        // Mettre à jour le store via localStorage (le store Zustand le récupérera)
        if (authData) {
          try {
            const parsed = JSON.parse(authData);
            parsed.state.token = newToken;
            localStorage.setItem('auth-storage', JSON.stringify(parsed));
          } catch {
            // Ignorer les erreurs de parse
          }
        }

        // Invalider le cache token en mémoire
        cachedToken = newToken;
        tokenCacheTime = Date.now();

        // Rejouer les requêtes en attente
        processQueue(null, newToken);

        // Rejouer la requête originale
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // S'assurer que la queue est vidée en rejetant toutes les promises
        isRefreshing = false;
        processQueue(refreshError, null);

        // Invalider le cache token
        cachedToken = null;
        tokenCacheTime = 0;

        // Refresh échoué — forcer la déconnexion
        try {
          const authStorage = localStorage.getItem('auth-storage');
          if (authStorage) {
            const parsed = JSON.parse(authStorage);
            parsed.state.user = null;
            parsed.state.token = null;
            parsed.state.refreshToken = null;
            parsed.state.isAuthenticated = false;
            localStorage.setItem('auth-storage', JSON.stringify(parsed));
          }
        } catch {
          // localStorage corrompu — supprimer directement
          localStorage.removeItem('auth-storage');
        }

        // Rediriger vers le login
        window.location.href = '/login';

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
